/* eslint-disable @typescript-eslint/consistent-type-definitions */
import type {
  ButtonProps,
  Theme as MuiTheme,
  PaletteColor,
  PaletteColorOptions,
  ThemeOptions,
} from "@mui/material";
import { createTheme } from "@mui/material";

import { utfTokens } from "./tokens";
import type { GraphKey } from "./tokens";

const mainFontFamily = utfTokens.font.main;
const altFontFamily = utfTokens.font.alt;
const condensedFontFamily = utfTokens.font.condensed;

const shadows = [
  "none",
  ...Array.from({ length: 24 })
    .fill("")
    .map((_, level) => `0 ${1 + level}px ${3 + level}px 0 rgba(0, 0, 0, ${0.16 + 0.01 * level})`),
] as ThemeOptions["shadows"];

// --- MUI Palette Augmentation for Custom Properties ---
// Custom palette colours are added at runtime with `augmentColor`,
// so they also need to be declared here for TypeScript. `palette.*` is for
// themed UI, while `palette.graph.*` contains chart colours.
declare module "@mui/material/styles/createPalette" {
  interface TypeBackground {
    selected: string;
    hover: string;
  }
  interface Palette {
    graph: Record<GraphKey, string>;
    border: { primary: string; light: string; divider: PaletteColor };
    red: PaletteColor;
    green: PaletteColor;
    blue: PaletteColor;
    teal: PaletteColor;
    yellow: PaletteColor;
    purple: PaletteColor;
    pink: PaletteColor;
    orange: PaletteColor;
  }
  interface PaletteOptions {
    graph?: Record<GraphKey, string>;
    border?: { primary?: string; light?: string; divider?: PaletteColorOptions };
    red?: PaletteColorOptions;
    green?: PaletteColorOptions;
    blue?: PaletteColorOptions;
    teal?: PaletteColorOptions;
    yellow?: PaletteColorOptions;
    purple?: PaletteColorOptions;
    pink?: PaletteColorOptions;
    orange?: PaletteColorOptions;
  }
}

// --- MUI Typography Augmentation for Semantic Variants ---
declare module "@mui/material/styles" {
  interface TypographyVariants {
    displayLarge: React.CSSProperties;
    displaySmall: React.CSSProperties;
    titleLarge: React.CSSProperties;
    titleMedium: React.CSSProperties;
    titleSmall: React.CSSProperties;
    labelLarge: React.CSSProperties;
    labelMedium: React.CSSProperties;
    bodyLarge: React.CSSProperties;
    bodyMedium: React.CSSProperties;
    bodySmall: React.CSSProperties;
  }
  interface TypographyVariantsOptions {
    displayLarge?: React.CSSProperties;
    displaySmall?: React.CSSProperties;
    titleLarge?: React.CSSProperties;
    titleMedium?: React.CSSProperties;
    titleSmall?: React.CSSProperties;
    labelLarge?: React.CSSProperties;
    labelMedium?: React.CSSProperties;
    bodyLarge?: React.CSSProperties;
    bodyMedium?: React.CSSProperties;
    bodySmall?: React.CSSProperties;
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    displayLarge: true;
    displaySmall: true;
    titleLarge: true;
    titleMedium: true;
    titleSmall: true;
    labelLarge: true;
    labelMedium: true;
    bodyLarge: true;
    bodyMedium: true;
    bodySmall: true;
  }
}

// --- The semantic type scale ---------------------------------------------------
// THE single source of type values for the whole app. Every legacy MUI variant below
// is an alias onto one of these ten, so there is exactly one set of numbers to edit.
//
// Platform's base font size is 14px (app.css sets `font-size: 87.5%`), so these px
// values sit on a 4px grid.
//
// `letterSpacing: "normal"` where it appears is deliberate — those variants render
// large numbers and card titles, where the designed tracking is visible.
//
// PropAI overrides all ten in propai.theme.ts.
const semanticType = {
  displayLarge: {
    // LargeNumber's main value.
    fontFamily: mainFontFamily,
    fontWeight: 500,
    fontSize: "80px",
    lineHeight: "96px",
    letterSpacing: "normal",
  },
  displaySmall: {
    // InfoCard and LargeNumber.
    fontFamily: mainFontFamily,
    fontWeight: 500,
    fontSize: "40px",
    lineHeight: "48px",
    letterSpacing: "normal",
  },
  titleLarge: {
    fontFamily: altFontFamily,
    fontWeight: 600,
    fontSize: "24px",
    lineHeight: "32px",
    letterSpacing: "0px",
  },
  titleMedium: {
    // Section headings, and SolidCard's title.
    fontFamily: altFontFamily,
    fontWeight: 600,
    fontSize: "16px",
    lineHeight: "24px",
    letterSpacing: "0px",
  },
  titleSmall: {
    // The workhorse: row titles, table cells, inline labels, and BaseCard's title.
    fontFamily: altFontFamily,
    fontWeight: 500,
    fontSize: "14px",
    lineHeight: "16px",
    letterSpacing: "normal",
  },
  labelLarge: {
    // BaseCard subtitle, UtfDateRangePicker, CardBottomText.
    fontFamily: mainFontFamily,
    fontWeight: 500,
    fontSize: "18px",
    lineHeight: "24px",
    letterSpacing: "normal",
  },
  labelMedium: {
    fontFamily: mainFontFamily,
    fontWeight: 500,
    fontSize: "12px",
    lineHeight: "16px",
    letterSpacing: "0.5px",
  },
  bodyLarge: {
    fontFamily: mainFontFamily,
    fontWeight: 300,
    fontSize: "16px",
    lineHeight: "24px",
    letterSpacing: "0.15px",
  },
  bodyMedium: {
    // Running text — paragraphs, descriptions, cell values.
    fontFamily: mainFontFamily,
    fontWeight: 300,
    fontSize: "14px",
    lineHeight: "20px",
    letterSpacing: "0.25px",
  },
  bodySmall: {
    fontFamily: mainFontFamily,
    fontWeight: 400,
    fontSize: "12px",
    lineHeight: "16px",
    letterSpacing: "0.4px",
  },
} satisfies Record<string, React.CSSProperties>;

// --- Create the theme ---
let baseTheme = createTheme({
  palette: {
    mode: "light",
    tonalOffset: 0.2,
    // To meet the minimum contrast of at least 4.5:1 as defined in WCAG 2.1 Rule 1.4.3
    contrastThreshold: 4.5,
    background: {
      default: utfTokens.background.default,
      paper: utfTokens.background.paper,
      selected: utfTokens.background.selected,
      hover: utfTokens.background.hover,
    },
    text: {
      primary: utfTokens.text.primary,
    },
    graph: utfTokens.graph,
    border: {
      primary: utfTokens.border.primary,
      light: utfTokens.border.light,
      divider: utfTokens.border.divider,
    },
  },
  typography: {
    fontFamily: mainFontFamily,
    // ── Legacy MUI variants — DERIVED. Do not author values here. ─────────────
    // These keys cannot be deleted: MUI's own components read them (`<Button>`
    // renders `typography.button`; ListItemText, TableCell and InputBase default
    // to body1/body2/caption). Each is aliased to the semantic variant nearest the
    // value it replaced, so one set of numbers exists and un-migrated call sites in
    // libs/components and propai-web follow the scale without being touched.
    //
    // `button` is the exception — Roboto Condensed bold, and the scale has no
    // condensed slot. It keeps its own values until one exists.
    h1: semanticType.displayLarge,
    h2: semanticType.displaySmall,
    h3: semanticType.labelLarge,
    h4: semanticType.titleMedium,
    h5: semanticType.titleMedium,
    h6: semanticType.titleSmall,
    subtitle1: semanticType.titleLarge,
    subtitle2: semanticType.titleMedium,
    body1: semanticType.bodyMedium,
    body2: semanticType.titleSmall,
    caption: semanticType.labelMedium,
    button: {
      fontFamily: condensedFontFamily,
      fontSize: ".929rem",
      fontWeight: "bold",
      fontStretch: "condensed",
      lineHeight: 1.23,
    },

    // ── The semantic type scale — edit values in `semanticType` above ─────────
    ...semanticType,
  },
  shape: {
    borderRadius: 2,
  },
  shadows,
});

// Palette & Typography -------------------------------------------------------
baseTheme = createTheme(baseTheme, {
  palette: {
    primary: baseTheme.palette.augmentColor({ color: utfTokens.brand.primary, name: "primary" }),
    secondary: baseTheme.palette.augmentColor({
      color: utfTokens.brand.secondary,
      name: "secondary",
    }),
    error: { main: utfTokens.semantic.error },
    success: { main: utfTokens.semantic.success },
    warning: { main: utfTokens.semantic.warning },
    info: { main: utfTokens.semantic.info },
    action: {
      // Override "disabled" colors with system palette
      disabled: baseTheme.palette.primary.contrastText,
      disabledBackground: baseTheme.palette.grey["400"],
    },
    border: {
      divider: baseTheme.palette.augmentColor({
        color: utfTokens.border.divider,
        name: "divider",
      }),
    },

    // UI accent colours → `palette.<name>`. NOT `palette.graph.*` — see tokens.ts
    // for why the two sets share names but hold different values.
    red: baseTheme.palette.augmentColor({ color: utfTokens.accent.red, name: "red" }),
    green: baseTheme.palette.augmentColor({ color: utfTokens.accent.green, name: "green" }),
    blue: baseTheme.palette.augmentColor({ color: utfTokens.accent.blue, name: "blue" }),
    teal: baseTheme.palette.augmentColor({ color: utfTokens.accent.teal, name: "teal" }),
    yellow: baseTheme.palette.augmentColor({ color: utfTokens.accent.yellow, name: "yellow" }),
    purple: baseTheme.palette.augmentColor({ color: utfTokens.accent.purple, name: "purple" }),
    pink: baseTheme.palette.augmentColor({ color: utfTokens.accent.pink, name: "pink" }),
    orange: baseTheme.palette.augmentColor({ color: utfTokens.accent.orange, name: "orange" }),
  },
});

// Overrides ------------------------------------------------------------------
baseTheme = createTheme(baseTheme, {
  components: {
    // MUI's `defaultVariantMapping` only knows the legacy variant names, so any
    // variant outside that list silently renders an inline `<span>` — losing the
    // block layout its legacy predecessor had (`body1`/`body2` were `<p>`, `h1`–`h6`
    // headings), which runs headings and paragraphs together on the page.
    //
    // `labelMedium` and `bodySmall` are `span` on purpose: both replaced `caption`,
    // which MUI also renders inline. A block element here would newly blockify chart
    // legends and inline table labels.
    //
    // `labelLarge` is `p`, not `h3` — it is also the BaseCard subtitle, and a subtitle
    // is not a heading. Sites needing heading semantics should pass `component`.
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          displayLarge: "h1",
          displaySmall: "h2",
          titleLarge: "h3",
          titleMedium: "h4",
          titleSmall: "h6",
          labelLarge: "p",
          labelMedium: "span",
          bodyLarge: "p",
          bodyMedium: "p",
          bodySmall: "span",
        },
      },
    },
    // Application-wide reset & custom styles
    MuiCssBaseline: {
      styleOverrides: {
        // Push Highcharts tooltip over MUI Drawer
        ".highcharts-tooltip-container": { zIndex: "1201 !important" },
        body: {
          // Global overrides for the Scrollbar
          "&::-webkit-scrollbar, & *::-webkit-scrollbar": {
            width: "0.6em",
            height: "0.6em",
            borderRadius: 5,
          },
          "&::-webkit-scrollbar-track, & *::-webkit-scrollbar-track": {
            borderRadius: 5,
            backgroundColor: baseTheme.palette.grey["50"],
            boxShadow: "inset 0 0 6px rgba(0, 0, 0, 0.2);",
          },
          "&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb": {
            borderRadius: 5,
            backgroundColor: baseTheme.palette.primary.main,
          },
          "&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover": {
            backgroundColor: baseTheme.palette.primary.light,
          },
          // Global overrides for HighCharts options button
          "& .highcharts-menu-item": {
            ...baseTheme.typography.h6,
          },
          "& .highcharts-data-table": {
            background: baseTheme.palette.grey["50"],
            "& > table": {
              color: baseTheme.palette.grey["800"],
              "& > caption": {
                ...baseTheme.typography.body2,
              },
              "& > thead": {
                ...baseTheme.typography.h5,
              },
              "& > tbody": {
                "& > tr": {
                  ...baseTheme.typography.h6,
                },
              },
            },
          },
        },
      },
    },
    // Ensure AppBar uses primary.dark by default so components that rely on theme.palette.primary.dark
    // receive a consistent background color without needing individual sx overrides.
    MuiAppBar: {
      styleOverrides: {
        // Apply primary.dark as the default root background so AppBar uses the themed dark primary color.
        root: {
          backgroundColor: baseTheme.palette.primary.dark,
        },
        // Also ensure color=primary variants resolve to the dark background
        colorPrimary: {
          backgroundColor: baseTheme.palette.primary.dark,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: ({ ownerState }: { ownerState: ButtonProps }) => ({
          ...(ownerState.variant === "outlined" && {
            "&.Mui-disabled": {
              color: baseTheme.palette.grey["400"],
            },
          }),
        }),
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          color: baseTheme.palette.grey["800"],
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          color: baseTheme.palette.grey["800"],
        },
      },
    },
    MuiPickersDay: {
      day: {
        color: "rgba(0, 0, 0, 0.54)",
      },
    },
    MuiPickersClockNumber: {
      clockNumber: {
        color: baseTheme.palette.grey["800"],
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          body: {
            color: baseTheme.palette.grey["800"],
          },
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          color: baseTheme.palette.grey["800"],
        },
      },
    },
    MuiTooltip: {
      // Global overrides for the Tooltip components
      styleOverrides: {
        tooltip: {
          maxWidth: 190,
          padding: baseTheme.spacing(1, 2),
          fontFamily: baseTheme.typography.body2.fontFamily,
          fontSize: 12,
          lineHeight: 1.3,
          fontWeight: baseTheme.typography.fontWeightMedium,
          color: baseTheme.palette.grey["50"],
          backgroundColor: baseTheme.palette.grey["600"],
        },
        /* Styles applied to the tooltip (label wrapper) element if `placement` contains "right". */
        tooltipPlacementRight: {
          transformOrigin: "left center",
          margin: "0 !important",
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          "& .MuiTabs-indicator": {
            backgroundColor: baseTheme.palette.secondary.main,
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          color: baseTheme.palette.primary.main,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          "&.Mui-selected": {
            background: baseTheme.palette.background.selected,
            "&:hover": {
              background: baseTheme.palette.background.selected,
            },
          },
          "&:hover": {
            background: baseTheme.palette.background.hover,
          },
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: "1.714rem",
          fontWeight: 600,
          lineHeight: 1.33,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          "& .MuiAlert-message": {
            paddingBlock: 12,
          },
        },
      },
    },
  },
});

// --- Exported Types for Custom Theme ---
export type UtfTheme = MuiTheme;

export const defaultTheme = baseTheme as unknown as UtfTheme;

// Types -----------------------------------------------
